import { inject } from '@angular/core';
import { signalStore, withState, withMethods } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { NotificationsApiService } from './notifications-api.service';
import type { NotificationItem, NotificationPreferenceProduct, NotificationProductFilter } from './notifications.models';
import { environment } from '@environment';

export const FEED_PAGE_SIZE = 20;
const DROPDOWN_SIZE = 5;

interface NotificationsState {
  unreadCount: number;
  // The bell dropdown's short recent slice — independent of the feed page.
  recent: NotificationItem[];
  recentStatus: 'idle' | 'loading' | 'ready' | 'error';
  // The full feed page.
  feed: NotificationItem[];
  feedStatus: 'idle' | 'loading' | 'ready' | 'error';
  feedPage: number;
  feedTotalPages: number;
  productFilter: NotificationProductFilter;
  // Preferences grid.
  preferences: NotificationPreferenceProduct[];
  preferencesStatus: 'idle' | 'loading' | 'ready' | 'error';
}

const initialState: NotificationsState = {
  unreadCount: 0,
  recent: [],
  recentStatus: 'idle',
  feed: [],
  feedStatus: 'idle',
  feedPage: 1,
  feedTotalPages: 1,
  productFilter: null,
  preferences: [],
  preferencesStatus: 'idle',
};

export const NotificationsStore = signalStore(
  { providedIn: 'root' },
  environment.storeWithDevTools('notifications'),
  withState<NotificationsState>(initialState),
  withMethods((store, api = inject(NotificationsApiService)) => {
    // Monotonic feed-request generation: only the response of the LATEST
    // loadFeed call may commit — a slower older response must not overwrite a
    // newer filter/page.
    let feedRequestId = 0;
    // Same guard for the unread count: the 60s poll, dropdown refresh, and
    // mutation resyncs overlap freely.
    let countRequestId = 0;
    // Bumped by every bulk read mutation; a single-row rollback from BEFORE the
    // bulk write must not resurrect an unread row afterwards.
    let readEpoch = 0;
    // One in-flight preference write per event: the DB follows arrival order,
    // the switch follows click order — chaining keeps them identical.
    const preferenceChains = new Map<string, Promise<boolean>>();

    const setReadLocally = (id: string, isRead: boolean): void => {
      const affects =
        store.recent().some(n => n.id === id && n.isRead !== isRead) ||
        store.feed().some(n => n.id === id && n.isRead !== isRead);
      updateState(store, isRead ? '[Notifications] Mark Read Local' : '[Notifications] Mark Read Revert', {
        recent: store.recent().map(n => (n.id === id ? { ...n, isRead } : n)),
        feed: store.feed().map(n => (n.id === id ? { ...n, isRead } : n)),
        unreadCount: affects ? Math.max(0, store.unreadCount() + (isRead ? -1 : 1)) : store.unreadCount(),
      });
    };

    return {
      async refreshUnreadCount(): Promise<void> {
        const requestId = ++countRequestId;
        const count = await api.getUnreadCount();
        if (requestId !== countRequestId) return;
        if (count !== null) updateState(store, '[Notifications] Unread Count', { unreadCount: count });
      },

      // The dropdown slice: latest few, any product, read state included.
      async loadRecent(): Promise<void> {
        updateState(store, '[Notifications] Recent Start', { recentStatus: 'loading' });
        const page = await api.getPage({ page: 1, limit: DROPDOWN_SIZE });
        if (!page) {
          updateState(store, '[Notifications] Recent Failed', { recentStatus: 'error' });
          return;
        }

        updateState(store, '[Notifications] Recent Ready', { recent: page.items, recentStatus: 'ready' });
      },

      async loadFeed(page = 1): Promise<void> {
        const requestId = ++feedRequestId;
        updateState(store, '[Notifications] Feed Start', { feedStatus: 'loading', feedPage: page });
        const result = await api.getPage({ page, limit: FEED_PAGE_SIZE, product: store.productFilter() });
        if (requestId !== feedRequestId) return;
        if (!result) {
          updateState(store, '[Notifications] Feed Failed', { feedStatus: 'error' });
          return;
        }

        updateState(store, '[Notifications] Feed Ready', {
          feed: result.items,
          feedStatus: 'ready',
          feedPage: result.page,
          feedTotalPages: Math.max(1, result.totalPages),
        });
      },

      // Unread items of one event type, newest first — for a host window that announces them.
      // ponytail: scans the first unread page only; a wire filter by event type when 20 is not enough.
      async unreadOfType(eventType: string): Promise<NotificationItem[]> {
        const result = await api.getPage({ page: 1, limit: FEED_PAGE_SIZE, unreadOnly: true });
        return result?.items.filter(n => n.eventType === eventType) ?? [];
      },

      async setProductFilter(filter: NotificationProductFilter): Promise<void> {
        updateState(store, '[Notifications] Product Filter', { productFilter: filter });
        await this.loadFeed(1);
      },

      // Optimistic: the row greys out immediately; a failed write rolls it back
      // so the row stays clickable for a retry — unless a bulk mark-all landed
      // meanwhile (its server state supersedes this row's failure).
      async markRead(id: string): Promise<void> {
        const target = [...store.recent(), ...store.feed()].find(n => n.id === id);
        if (target?.isRead) return;
        // A row outside both lists (read by a host window): the server count is the only local state to move.
        if (!target) {
          if (await api.markRead(id)) await this.refreshUnreadCount();
          return;
        }

        const epoch = readEpoch;
        setReadLocally(id, true);
        const ok = await api.markRead(id);
        if (!ok && epoch === readEpoch) setReadLocally(id, false);
      },

      async markAllRead(): Promise<void> {
        readEpoch += 1;
        updateState(store, '[Notifications] Read All Local', {
          recent: store.recent().map(n => ({ ...n, isRead: true })),
          feed: store.feed().map(n => ({ ...n, isRead: true })),
          unreadCount: 0,
        });
        const ok = await api.markAllRead();
        // A failed write must restore the true rows, not leave them greyed out.
        if (!ok) await Promise.all([this.loadFeed(store.feedPage()), this.loadRecent()]);
        // Re-sync either way: a 60s-poll response in flight during the local
        // zeroing can land after it and resurrect the stale count.
        await this.refreshUnreadCount();
      },

      async loadPreferences(): Promise<void> {
        updateState(store, '[Notifications] Preferences Start', { preferencesStatus: 'loading' });
        const grid = await api.getPreferences();
        if (!grid) {
          updateState(store, '[Notifications] Preferences Failed', { preferencesStatus: 'error' });
          return;
        }

        updateState(store, '[Notifications] Preferences Ready', { preferences: grid, preferencesStatus: 'ready' });
      },

      // Optimistic toggle with rollback — the switch must not lie about a failed
      // save. Writes for one event are chained so a double-click cannot reach
      // the server in reverse order.
      async togglePreference(product: string, eventType: string, emailEnabled: boolean): Promise<boolean> {
        const apply = (value: boolean): void => {
          updateState(store, '[Notifications] Preference Flip', {
            preferences: store
              .preferences()
              .map(p =>
                p.product === product
                  ? { ...p, events: p.events.map(e => (e.eventType === eventType ? { ...e, emailEnabled: value } : e)) }
                  : p,
              ),
          });
        };

        apply(emailEnabled);
        const chainKey = `${product}:${eventType}`;
        const run = async (): Promise<boolean> => {
          const ok = await api.setPreference(product, eventType, emailEnabled);
          if (!ok) apply(!emailEnabled);
          return ok;
        };

        const chained = (preferenceChains.get(chainKey) ?? Promise.resolve(true)).then(run, run);
        preferenceChains.set(chainKey, chained);
        return chained;
      },
    };
  }),
);
